import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

/**
 * Tiny contentEditable-based notes editor that supports:
 *   - Bold (Ctrl/Cmd + B or toolbar)
 *   - Bullet list (toolbar)
 *   - New lines (Enter)
 *
 * Output is an HTML string; consume via `value` / `onChange`. Empty
 * editor produces "" (not "<br>" or "<p></p>") so required-field
 * validation works naturally.
 */
const stripEmpty = (html) => {
  if (!html) return "";
  // Treat a wrapper element that contains only zero-width / br as empty
  const trimmed = html
    .replace(/<br\s*\/?>(\s|&nbsp;)*$/i, "")
    .replace(/(\s|&nbsp;)+/g, " ")
    .trim();
  // Remove tags to test for actual text
  const textOnly = trimmed.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, "").trim();
  return textOnly.length === 0 ? "" : html;
};

const ToolButton = ({ title, onClick, Icon, active }) => (
  <Tooltip title={title} arrow>
    <IconButton
      size="small"
      // Use mousedown so the editor doesn't lose focus before the
      // command runs — execCommand needs the selection still alive.
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      sx={{
        color: active ? "#4D6BF6" : "#374151",
        backgroundColor: active ? "#EEF2FF" : "transparent",
        borderRadius: "8px",
        padding: "4px",
        "&:hover": {
          backgroundColor: active ? "#E0E7FF" : "#F3F4F6",
        },
      }}
    >
      <Icon sx={{ fontSize: 18 }} />
    </IconButton>
  </Tooltip>
);

const RichTextEditor = ({
  value = "",
  onChange,
  placeholder = "Write notes…",
  label,
  error = false,
  helperText = "",
  minHeight = 110,
}) => {
  const editorRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [activeMarks, setActiveMarks] = useState({
    bold: false,
    bullet: false,
  });

  // Keep the editor's HTML in sync with the controlled `value` prop —
  // but only when the editor doesn't already have that exact HTML
  // (otherwise we'd reset the caret on every keystroke).
  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.innerHTML !== (value || "")) {
      editorRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => {
    if (!editorRef.current) return;
    const html = stripEmpty(editorRef.current.innerHTML);
    onChange?.(html);
  };

  const refreshActiveMarks = () => {
    try {
      setActiveMarks({
        bold: document.queryCommandState("bold"),
        bullet: document.queryCommandState("insertUnorderedList"),
      });
    } catch {
      // queryCommandState throws on detached iframes — safe to ignore.
    }
  };

  const exec = (cmd) => () => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, null);
    refreshActiveMarks();
    emit();
  };

  const handleKeyDown = (e) => {
    // Ctrl/Cmd+B shortcut for bold — execCommand handles it natively,
    // but we still need to refresh the toolbar's active state.
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
      // Let the default behavior run; defer refresh to next tick.
      setTimeout(refreshActiveMarks, 0);
    }
  };

  const borderColor = error
    ? "#EF4444"
    : focused
    ? "#4D6BF6"
    : "#D7D8D9";

  return (
    <Box sx={{ width: "100%", position: "relative" }}>
      {label && (
        <Typography
          sx={{
            position: "absolute",
            top: -8,
            left: 12,
            background: "#fff",
            padding: "0 6px",
            fontSize: 12,
            color: error ? "#EF4444" : "#0D0D0E",
            fontFamily: "Inter Tight",
            zIndex: 1,
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          background: "#fff",
          transition: "border-color 0.15s",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            padding: "6px 8px",
            borderBottom: "1px solid #F1F2F4",
          }}
        >
          <ToolButton
            title="Bold (Ctrl/Cmd+B)"
            onClick={exec("bold")}
            Icon={FormatBoldIcon}
            active={activeMarks.bold}
          />
          <ToolButton
            title="Bullet list"
            onClick={exec("insertUnorderedList")}
            Icon={FormatListBulletedIcon}
            active={activeMarks.bullet}
          />
        </Box>

        {/* The editor itself */}
        <Box
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          spellCheck
          // Placeholder rendered via CSS — we set `data-placeholder` and
          // pair with the `:empty::before` selector below.
          data-placeholder={placeholder}
          onFocus={() => {
            setFocused(true);
            refreshActiveMarks();
          }}
          onBlur={() => setFocused(false)}
          onInput={() => {
            refreshActiveMarks();
            emit();
          }}
          onKeyUp={refreshActiveMarks}
          onMouseUp={refreshActiveMarks}
          onKeyDown={handleKeyDown}
          sx={{
            minHeight,
            padding: "10px 12px",
            fontSize: 14,
            fontFamily: "Inter Tight",
            color: "#0D0D0E",
            outline: "none",
            lineHeight: 1.5,
            // Bullets in WebKit need an explicit left padding for
            // markers to show.
            "& ul": { paddingLeft: "20px", margin: 0 },
            "& ol": { paddingLeft: "20px", margin: 0 },
            "& strong, & b": { fontWeight: 600 },
            "&:empty::before": {
              content: "attr(data-placeholder)",
              color: "#9CA3AF",
              pointerEvents: "none",
            },
          }}
        />
      </Box>
      {helperText && (
        <Typography
          sx={{
            fontSize: 12,
            color: error ? "#EF4444" : "#6B7280",
            fontFamily: "Inter Tight",
            mt: 0.5,
            ml: 1.5,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default RichTextEditor;
